import {
  SelectTrigger,
  InputNumberWithButtons,
  ListBoxItem,
  ResourcePickerPopover,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { VoiceGeneralFormValues } from "../../types";
import {
  DAILY_CALL_LIMIT_OPTIONS,
  MAX_CALL_DURATION_MIN,
  MAX_CONCURRENT_CALLS,
  MIN_CALLS_LIMIT,
} from "../../utils/getVoiceFormValues";

type DailyLimitOption = {
  id: string;
  value: number | null;
  label: string;
};

export const CallsLimitSection: React.FC = () => {
  const { t } = useTranslations();
  const { control } = useFormContext<VoiceGeneralFormValues>();

  const dailyLimitOptions = useMemo<ListBoxItem<DailyLimitOption>[]>(
    () => [
      {
        id: "unlimited",
        value: {
          id: "unlimited",
          value: null,
          label: t({
            id: "common.unlimited",
            defaultMessage: "Unlimited",
          }),
        },
      },
      ...DAILY_CALL_LIMIT_OPTIONS.map((limit) => ({
        id: String(limit),
        value: {
          id: String(limit),
          value: limit,
          label: t(
            {
              id: "client-support-page.daily-calls-limit-calls",
              defaultMessage: "{count} Calls",
            },
            { count: limit }
          ),
        },
      })),
    ],
    [t]
  );

  return (
    <CRMInfoSection
      title={t({
        id: "client-support-page.calls-limit",
        defaultMessage: "Calls Limit",
      })}
      subtitle={t({
        id: "client-support-page.calls-limit-description",
        defaultMessage:
          "Set limits on call duration and volume to manage usage and ensure smooth performance.",
      })}
    >
      <div className="flex flex-col gap-6 max-lg:gap-4">
        <Controller
          control={control}
          name="daily_calls_limit"
          render={({ field: { value, onChange } }) => {
            const selected =
              dailyLimitOptions.find((option) => option.value.value === value)
                ?.value ?? dailyLimitOptions[0].value;

            return (
              <div className="flex flex-wrap items-center gap-2 max-lg:gap-1.5">
                <span className="w-45 shrink-0 font-secondary text-sm text-black max-lg:w-full">
                  {t({
                    id: "client-support-page.daily-calls-limit",
                    defaultMessage: "Daily Calls limit",
                  })}
                </span>
                <ResourcePickerPopover
                  key={selected.id}
                  className="w-38"
                  renderTrigger={({ Trigger, triggerRef, isOpen }) => (
                    <Trigger
                      ref={triggerRef}
                      as={SelectTrigger}
                      label={selected.label}
                      hasValue
                      isOpen={isOpen}
                      className="w-max py-2"
                    />
                  )}
                  items={dailyLimitOptions}
                  selectionBehavior={{ mode: "single", canToggle: false }}
                  defaultSelectedIds={[selected.id]}
                  maxVisibleItems={6}
                  itemSize="md"
                  spaceBetweenItems="compact"
                  renderLabel={(item) => item.value.label}
                  onSelect={({ item, onClose }) => {
                    onChange(item.value.value);
                    onClose();
                  }}
                />
              </div>
            );
          }}
        />

        <Controller
          control={control}
          name="max_call_duration_min"
          render={({ field: { value, onChange } }) => (
            <div className="flex flex-wrap items-center gap-2 max-lg:gap-1.5">
              <span className="w-45 shrink-0 font-secondary text-sm text-black max-lg:w-full">
                {t({
                  id: "client-support-page.max-call-duration-min",
                  defaultMessage: "Max Call duration (min)",
                })}
              </span>
              <InputNumberWithButtons
                value={value}
                min={MIN_CALLS_LIMIT}
                max={MAX_CALL_DURATION_MIN}
                onChange={onChange}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="max_concurrent_calls"
          render={({ field: { value, onChange } }) => (
            <div className="flex flex-wrap items-center gap-2 max-lg:gap-1.5">
              <span className="w-45 shrink-0 font-secondary text-sm text-black max-lg:w-full">
                {t({
                  id: "client-support-page.max-no-of-calls-at-a-time",
                  defaultMessage: "Max no. of Calls at a time",
                })}
              </span>
              <InputNumberWithButtons
                value={value}
                min={MIN_CALLS_LIMIT}
                max={MAX_CONCURRENT_CALLS}
                onChange={onChange}
              />
            </div>
          )}
        />
      </div>
    </CRMInfoSection>
  );
};
